import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useProjects } from "@/contexts/ProjectContext";
import {
  ALL_PROJECTS_CONVERSATION_SCOPE,
  PERSONAL_CONVERSATION_SCOPE,
  conversationScopeIdentity,
  getConversationRequestScope,
  type ConversationScope,
} from "@/features/playground/conversation-query-keys";

/**
 * The scope a conversations query has to be keyed by.
 *
 * Conversation server state is partitioned by the authenticated principal and
 * the organization/project headers the request actually sends. Bootstrap must
 * finish before consumers issue requests so stale browser storage is never used
 * as an identity source.
 */
export const useConversationScope = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const { currentOrganization, initialized } = useOrganization();
  const { selectedProjects } = useProjects();

  const organizationId =
    currentOrganization?.id ?? PERSONAL_CONVERSATION_SCOPE;

  const projectId =
    selectedProjects.length === 1 && selectedProjects[0] !== "all"
      ? selectedProjects[0]
      : ALL_PROJECTS_CONVERSATION_SCOPE;

  const scope: ConversationScope = {
    principalId: currentUser?.id ?? "",
    organizationId,
    projectId,
  };

  return {
    scope,
    identity: conversationScopeIdentity(scope),
    requestScope: getConversationRequestScope(scope),
    ready:
      authLoading !== true &&
      initialized === true &&
      Boolean(scope.principalId),
  };
};
